import { Appointment } from '../../../../core/models/appointment';
import { AppointmentStatus } from '../../../../core/models/appointment-status.enum';
import { getFormattedClockDate } from '../../../../core/utils/date.utils';
import { Form, Input, Modal } from 'antd';
import { sendAppointmentRequest } from '../../../../core/services/appointment.service';
import { sendMail } from '../../../../core/services/mail.service';
import { StartTimeEventEmit } from 'react-schedule-meeting';
import { useEffect } from 'react';
import { useState } from '@wordpress/element';
import { useTranslation } from 'react-i18next';
import './appointment-customer-modal.scss';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import PhoneInput from 'antd-phone-input';
import FormItem from 'antd/es/form/FormItem';
import { SvgIcon } from '../../../svg-icon/svg-icon';

type AppointmentCustomerModalProps = {
	isOpen: boolean;
	startTimeEvent?: StartTimeEventEmit;
	onClose: () => void;
	onSubmitAppointment: (appointment: Appointment) => void;
};

export const AppointmentCustomerModal: React.FC<AppointmentCustomerModalProps> = ({ isOpen, startTimeEvent, onClose, onSubmitAppointment }) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState<boolean>(false);
	const [form] = Form.useForm();

	useEffect(() => {
		if (isOpen) {
			setOpen(true);
			onClose();
		}
	}, [isOpen, onClose]);

	async function handleSubmitAppointment() {
		const formValues = await form.validateFields();

		if (!startTimeEvent) return;

		const newAppointment: Appointment = {
			startTime: dayjs(startTimeEvent.startTime).second(0).toDate(),
			status: AppointmentStatus.REQUEST,
			notes: formValues.notes,
			customer: {
				firstname: formValues.firstName,
				lastname: formValues.lastName,
				email: formValues.mail,
				phoneNumber: formValues.phoneNumber.phoneNumber
					? `+${formValues.phoneNumber.countryCode} ${formValues.phoneNumber.areaCode} ${formValues.phoneNumber.phoneNumber}`
					: undefined,
			},
		};

		setOpen(false);
		sendMailNotifications(newAppointment);

		sendAppointmentRequest(newAppointment).then(() => {
			form.resetFields();
			onSubmitAppointment(newAppointment);
		});
	}

	function sendMailNotifications(newAppointment: Appointment) {
		if (window.bookrPluginData.notifications.sendAdminRequestMail) {
			sendMail({
				to: window.bookrPluginData.adminEmail,
				subject: t('backEnd.mailTemplate.appointmentRequest.admin.subject'),
				body: t('backEnd.mailTemplate.appointmentRequest.admin.body', {
					startTime: getFormattedClockDate(newAppointment.startTime),
					firstName: newAppointment.customer?.firstname,
					lastName: newAppointment.customer?.lastname,
					notes: newAppointment.notes ?? '-',
					adminAppointmentUrl: `${window.location.origin}/wp-admin/admin.php?page=bookr`,
				}),
			});
		}

		if (window.bookrPluginData.notifications.sendCustomerRequestMail) {
			sendMail({
				to: newAppointment.customer?.email ?? '',
				subject: t('backEnd.mailTemplate.appointmentRequest.customer.subject'),
				body: t('backEnd.mailTemplate.appointmentRequest.customer.body'),
			});
		}
	}

	return (
		<Modal
			title={t('frontEnd.calendar.appointmentRequest')}
			open={open}
			onOk={handleSubmitAppointment}
			okText={t('frontEnd.calendar.confirmAppointment')}
			onCancel={() => setOpen(false)}
			cancelText={t('common.cancel')}
			className='appointment-customer-modal'
		>
			<div className='confirm-appointment'>
				<span>
					<b>{t('frontEnd.calendar.confirmAppointmentDescription')}</b>
				</span>
				<div className='start-time-container'>
					<SvgIcon name='calendar' size={16} />
					<span>{getFormattedClockDate(startTimeEvent?.startTime)}</span>
				</div>
			</div>

			<Form form={form} layout='vertical' onFinish={handleSubmitAppointment} className='form-container'>
				<div className='form-row'>
					<Form.Item label={t('common.firstName')} name='firstName' rules={[{ required: true, message: t('frontEnd.formValidations.firstName') }]}>
						<Input placeholder={t('common.firstName')} />
					</Form.Item>
					<Form.Item label={t('common.lastName')} name='lastName' rules={[{ required: true, message: t('frontEnd.formValidations.lastName') }]}>
						<Input placeholder={t('common.lastName')} />
					</Form.Item>
				</div>

				<div className='form-row'>
					<Form.Item
						label={t('common.mail')}
						name='mail'
						rules={[
							{ required: true, message: t('frontEnd.formValidations.mail') },
							{
								pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
								message: t('frontEnd.formValidations.validMail'),
							},
						]}
					>
						<Input placeholder={t('common.mail')} />
					</Form.Item>
				</div>

				<div className='form-row'>
					<FormItem label={t('common.phoneNumber')} name='phoneNumber'>
						<PhoneInput enableSearch enableArrow />
					</FormItem>
				</div>

				<div className='form-row'>
					<FormItem label={t('common.notes')} name='notes'>
						<TextArea rows={4} placeholder={t('common.notes')} />
					</FormItem>
				</div>
			</Form>
		</Modal>
	);
};
